import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../common/Modal';
import { FileUploadZone } from '../pilgrimGuide/upload/FileUploadZone';
import { ValidationErrors } from '../pilgrimGuide/upload/ValidationErrors';
import { WarningMessage } from '../pilgrimGuide/upload/WarningMessage';
import { Download, Copy, Check, Building2, Home } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePackagesStore } from '../../stores/packagesStore';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const BulkUpgradeModal = ({ isOpen, onClose }: Props) => {
  const { translations } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [copiedHotel, setCopiedHotel] = useState<string | null>(null);
  const [copiedRoomType, setCopiedRoomType] = useState<string | null>(null);
  const [availableHotels, setAvailableHotels] = useState<string[]>([]);
  const navigate = useNavigate();
  const { packages, fetchPackages } = usePackagesStore();

  // Room types that can be copied
  const roomTypes = [
    { id: 'double', label: 'double' },
    { id: 'triple', label: 'triple' },
    { id: 'quadruple', label: 'quadruple' }
  ];

  // Fetch packages to extract available hotels
  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen, fetchPackages]);

  // Extract unique hotels from packages
  useEffect(() => {
    const uniqueHotels = new Set<string>();
    
    Object.values(packages).forEach(pkg => {
      // Add main Makkah hotel
      if (pkg.mainMakkahHotel) {
        uniqueHotels.add(pkg.mainMakkahHotel);
      }
      
      // Add shifting Makkah hotel if exists
      if (pkg.shiftingMakkahHotel) {
        uniqueHotels.add(pkg.shiftingMakkahHotel);
      }
      
      // Add Madinah hotel
      if (pkg.madinahHotel) {
        uniqueHotels.add(pkg.madinahHotel);
      }
    });
    
    setAvailableHotels(Array.from(uniqueHotels).sort());
  }, [packages]);

  const handleDownloadTemplate = () => {
    const template = [
      {
        passport_number: '123456789',
        hotel_name: 'Grand Plaza Hotel',
        room_type: 'double' // Target room type (double or triple)
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'bulk-upgrade-template.xlsx');
  };

  const handleCopyHotelName = async (hotelName: string) => {
    try {
      await navigator.clipboard.writeText(hotelName);
      setCopiedHotel(hotelName);
      setTimeout(() => setCopiedHotel(null), 2000);
      toast.success(translations?.bulkUpgrade?.copySuccess || 'Hotel name copied');
    } catch (error) {
      toast.error(translations?.bulkUpgrade?.copyError || 'Failed to copy');
    }
  };

  const handleCopyRoomType = async (roomType: string) => {
    try {
      await navigator.clipboard.writeText(roomType);
      setCopiedRoomType(roomType);
      setTimeout(() => setCopiedRoomType(null), 2000);
      toast.success(translations?.bulkUpgrade?.copyTypeSuccess || 'Room type copied');
    } catch (error) {
      toast.error(translations?.bulkUpgrade?.copyError || 'Failed to copy');
    }
  };

  const handleUpload = () => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      if (data) {
        // Close modal first
        onClose();
        
        // Navigate to status page with file data
        navigate('/bulk-upgrade-status', {
          state: { fileData: data }
        });
      }
    };
    reader.readAsBinaryString(file);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={translations?.roomUpgrade?.bulkUpgrade?.title || 'Bulk Room Upgrade'}
    >
      <div className="space-y-6">
        {/* Room Types For Copying */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-medium mb-3">
            {translations?.bulkUpgrade?.roomTypes || 'Room Types'}
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {roomTypes.map((type) => (
              <motion.div
                key={type.id}
                whileHover={{ scale: 1.02 }}
                className="bg-white p-3 rounded-lg shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Home className="text-primary-500" size={18} />
                    <span className="font-medium">{type.label}</span>
                  </div>
                  <button
                    onClick={() => handleCopyRoomType(type.label)}
                    className="p-1.5 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                  >
                    {copiedRoomType === type.label ? (
                      <Check size={16} className="text-green-500" />
                    ) : (
                      <Copy size={16} />
                    )}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Available Hotels */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-medium mb-3">
            {translations?.bulkUpgrade?.availableHotels || 'Available Hotels'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2">
            {availableHotels.map((hotel) => (
              <motion.div
                key={hotel}
                whileHover={{ scale: 1.02 }}
                className="bg-white p-3 rounded-lg shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Building2 className="text-primary-500 shrink-0" size={18} />
                    <span className="font-medium truncate" title={hotel}>{hotel}</span>
                  </div>
                  <button
                    onClick={() => handleCopyHotelName(hotel)}
                    className="p-1.5 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 shrink-0"
                  >
                    {copiedHotel === hotel ? (
                      <Check size={16} className="text-green-500" />
                    ) : (
                      <Copy size={16} />
                    )}
                  </button>
                </div>
              </motion.div>
            ))}
            
            {availableHotels.length === 0 && (
              <div className="col-span-2 text-center py-4 text-gray-500">
                {translations?.bulkUpgrade?.noHotels || 'No hotels available'}
              </div>
            )}
          </div>
        </div>

        {/* Template Download */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>{translations?.roomUpgrade?.bulkUpgrade?.downloadTemplate || 'Download Excel Template'}</span>
        </button>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations?.roomUpgrade?.bulkUpgrade?.fileLabel || 'Upload Excel File'}
          </label>
          <FileUploadZone
            file={file}
            onFileSelect={setFile}
            onFileRemove={() => {
              setFile(null);
              setValidationErrors([]);
            }}
          />
        </div>

        <ValidationErrors errors={validationErrors} />
        
        {/* Instructions */}
        <div className="bg-blue-50 p-4 rounded-lg text-blue-700">
          <h4 className="font-medium mb-2">
            {translations?.roomUpgrade?.bulkUpgrade?.instructions?.title || 'Instructions'}
          </h4>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>
              {translations?.roomUpgrade?.bulkUpgrade?.instructions?.passportRequired || 
                'Passport number is required for each pilgrim'}
            </li>
            <li>
              {translations?.roomUpgrade?.bulkUpgrade?.instructions?.hotelRequired || 
                'Hotel name should match exactly the name in the system'}
            </li>
            <li>
              {translations?.roomUpgrade?.bulkUpgrade?.instructions?.roomTypeRequired || 
                'Room type must be either "double" or "triple"'}
            </li>
            <li>
              {translations?.roomUpgrade?.bulkUpgrade?.instructions?.upgradeLimitation || 
                'Only pilgrims in triple or quadruple rooms can be upgraded'}
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.roomUpgrade?.bulkUpgrade?.cancel || 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file}
            className={`px-6 py-2 rounded-lg text-white
              ${!file
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {translations?.roomUpgrade?.bulkUpgrade?.upload || 'Upload'}
          </button>
        </div>
      </div>
    </Modal>
  );
};